import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => NumberCellInputComponent),
	multi: true,
};

@Component({
	selector: "ic-number-input-cell",
	styleUrls: ["./number-cell.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./number-cell.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class NumberCellInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);
	private formBuilder = inject(UntypedFormBuilder);

	disabled = false;
	form = this.formBuilder.group({
		value: [""],
	});

	private readonly destroy$ = new Subject<void>();

	@Input() errorText = "";
	@Input() suffix = "";
	@Input() min: number;
	@Input() max: number;

	@Output() blurEvent = new EventEmitter();
	@Output() focusEvent = new EventEmitter();

	@ContentChild("suffixTmpl", { static: false }) suffixTemplate: TemplateRef<any>;
	@ContentChild("labelTmpl", { static: false }) labelTemplate: TemplateRef<any>;

	@ViewChild("input", { static: true }) input: ElementRef;

	@HostBinding("class.error") @Input() error = false;
	@HostBinding("class.is-hidden") hideLabel = true;

	writeValue(value: number) {
		// Prevent emit so parent form is not marked as dirty
		if (typeof value !== "number") {
			this.form.reset({ value: null }, { emitEvent: false });
		} else {
			this.form.patchValue({ value }, { emitEvent: false });
		}

		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		// Prevent emit so parent form is not marked as dirty
		if (isDisabled) {
			this.form.disable({ emitEvent: false });
		} else {
			this.form.enable({ emitEvent: false });
		}

		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	showInput() {
		this.hideLabel = false;
		this.focusEvent.emit();
	}

	focus() {
		this.input.nativeElement.focus();
	}

	onBlurred() {
		this.hideLabel = true;
		this.cdRef.markForCheck();
		this.onTouch();
		this.blurEvent.emit();
	}

	get label() {
		const value = this.form.get("value").value;
		const rawValue = this.input.nativeElement.value;

		return typeof value === "number" ? value : rawValue || ".";
	}

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {}; // eslint-disable-line @typescript-eslint/no-empty-function

	ngOnInit() {
		this.form
			.get("value")
			.valueChanges.pipe(
				tap((value) => {
					this.onModelChange(value);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
